import { defineStore } from 'pinia';
import { useAccountStore } from '@/stores/account';
import { useUserStore, USER_SETTINGS_KEYS } from '@/stores/user';
import { widgetPresets, availableWidgets } from '@/data/widgetPresets';

export const useCompanyWidgetsStore = defineStore('companyWidgets', {
    state: () => {
        const accountStore = useAccountStore();
        const accountId = accountStore?.account?.account_id ? accountStore.account.account_id : null;
        const userProspectDetailsSettings = useUserStore().getSettings(USER_SETTINGS_KEYS.prospectDetails, accountId);

        const defaultPreset = widgetPresets.find(p => p.id === 'default');
        const defaultOptions = {
            activePresetId: 'default',
            widgets: defaultPreset.widgets.map(pw => ({
                id: pw.id,
                defaultToggle: pw.defaultToggle,
            }))
        };

        let mergedOptions;
        if (userProspectDetailsSettings?.options?.widgets) {
            const userWidgets = userProspectDetailsSettings.options.widgets || [];

            const availableIds = availableWidgets.map(w => w.id);
            const filteredUserWidgets = userWidgets.filter(w => availableIds.includes(w.id));

            const userWidgetIds = filteredUserWidgets.map(w => w.id);
            const missingWidgets = defaultOptions.widgets.filter(w => !userWidgetIds.includes(w.id));

            const missingSessions = missingWidgets.filter(w => w.id.includes('prospect_sessions'));
            const otherMissing = missingWidgets.filter(w => !w.id.includes('prospect_sessions'));

            mergedOptions = {
                activePresetId: userProspectDetailsSettings.options.activePresetId ?? null,
                widgets: [...missingSessions, ...filteredUserWidgets, ...otherMissing]
            };
        } else {
            mergedOptions = { ...defaultOptions };
        }

        return {
            componentRefs: {},
            options: mergedOptions,
        }
    },
    actions: {
        saveProspectDetailsSettings() {
            const userStore = useUserStore();
            const accountStore = useAccountStore();
            userStore.saveSettings(USER_SETTINGS_KEYS.prospectDetails, accountStore.account.account_id, { options: this.options });
        },
        getWidgetOrder() {
            if (this.options.widgets && Array.isArray(this.options.widgets)) {
                return this.options.widgets.map(widget => widget.id);
            }
            return availableWidgets.map(widget => widget.id);
        },
        setWidgetOrder(newOrder) {
            if (!Array.isArray(newOrder)) return false;

            const allWidgetIds = availableWidgets.map(widget => widget.id);
            const isValidOrder = newOrder.every(id => allWidgetIds.includes(id)) &&
                                allWidgetIds.every(id => newOrder.includes(id));

            if (!isValidOrder) return false;

            const defaultPreset = widgetPresets.find(p => p.id === 'default');
            const currentWidgets = this.options.widgets || defaultPreset.widgets.map(pw => ({
                id: pw.id,
                defaultToggle: pw.defaultToggle,
            }));

            this.options.widgets = newOrder.map(id => {
                const existingWidget = currentWidgets.find(w => w.id === id);
                return existingWidget || {
                    id,
                    defaultToggle: defaultPreset.widgets.find(w => w.id === id)?.defaultToggle ?? false,
                };
            });

            this.options.activePresetId = null;

            this.saveProspectDetailsSettings();
            return true;
        },
        moveWidgetUp(widgetId) {
            const order = this.getWidgetOrder();
            const index = order.indexOf(widgetId);
            if (index > 0) {
                const newOrder = [...order];
                [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
                const result = this.setWidgetOrder(newOrder);
                
                // Force reactive update of visible widgets
                this.$patch({});
                
                return result;
            }
            return false;
        },
        
        moveWidgetDown(widgetId) {
            const order = this.getWidgetOrder();
            const index = order.indexOf(widgetId);
            if (index >= 0 && index < order.length - 1) {
                const newOrder = [...order];
                [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
                const result = this.setWidgetOrder(newOrder);
                
                // Force reactive update of visible widgets
                this.$patch({});
                
                return result;
            }
            return false;
        },
        getWidgetToggle(widgetId) {
            if (this.options?.widgets) {
                const widgetConfig = this.options.widgets.find(w => w.id === widgetId);
                if (widgetConfig && 'defaultToggle' in widgetConfig) {
                    return widgetConfig.defaultToggle;
                }
            }
            const defaultPreset = widgetPresets.find(p => p.id === 'default');
            return defaultPreset?.widgets.find(w => w.id === widgetId)?.defaultToggle ?? false;
        },
        setWidgetToggle(widgetId, isToggled) {
            if (!this.options.widgets) {
                const defaultPreset = widgetPresets.find(p => p.id === 'default');
                this.options.widgets = defaultPreset.widgets.map(pw => ({
                    id: pw.id,
                    defaultToggle: pw.defaultToggle,
                }));
            }
            
            const widgetIndex = this.options.widgets.findIndex(w => w.id === widgetId);
            
            if (widgetIndex >= 0) {
                this.options.widgets[widgetIndex] = {
                    ...this.options.widgets[widgetIndex],
                    defaultToggle: !!isToggled
                };
            } else if (availableWidgets.some(w => w.id === widgetId)) {
                this.options.widgets.push({
                    id: widgetId,
                    defaultToggle: !!isToggled
                });
            } else {
                return false;
            }
            
            this.saveProspectDetailsSettings();
            return true;
        },
        applyPreset(presetId) {
            const preset = widgetPresets.find(p => p.id === presetId);
            if (!preset) return;

            this.options.widgets = preset.widgets.map(pw => ({
                id: pw.id,
                defaultToggle: pw.defaultToggle,
            }));
            this.options.activePresetId = presetId;
            this.saveProspectDetailsSettings();

            preset.widgets.forEach(pw => {
                if (pw.defaultToggle) {
                    this.openWidget(pw.id);
                } else {
                    this.closeWidget(pw.id);
                }
            });

            if (preset.widgets.length > 0) {
                const firstWidgetId = preset.widgets[0].id;
                setTimeout(() => {
                    this.navigateToWidget(firstWidgetId);
                }, 200);
            }
        },
        resetWidgetConfig() {
            this.applyPreset('default');
        },
        registerComponentRef(widgetId, componentRef) {
            if (componentRef) {
                this.componentRefs[widgetId] = componentRef;
            } else {
                delete this.componentRefs[widgetId];
            }
        },
        getComponentRef(widgetId) {
            return this.componentRefs[widgetId];
        },
        getAllComponentRefs() {
            return this.componentRefs;
        },
        getActiveWidgets() {
            const widgetOrder = this.getWidgetOrder();
            return widgetOrder.map(id => {
                const component = this.componentRefs[id];
                const widgetDef = availableWidgets.find(w => w.id === id);
                const widgetSection = component?.$refs?.WidgetSection;

                const isUnavailable = component
                    && typeof component.unavailable === 'boolean'
                    && component.unavailable;

                return {
                    id,
                    titleKey: widgetDef?.props?.titleKey || id,
                    unavailable: !!isUnavailable,
                    loading: widgetSection ? !!widgetSection.loading : false,
                    loaded: widgetSection ? !!widgetSection.loaded : false,
                    toggle: widgetSection ? !!widgetSection.toggle : false,
                    paywallActive: (!!component?.paywall && !!component?.paywallActive) ? true : false,
                };
            });
        },
        getWidgets() {
            const widgetOrder = this.getWidgetOrder();
            const orderedWidgets = [...availableWidgets]
                .sort((a, b) => {
                    const indexA = widgetOrder.indexOf(a.id);
                    const indexB = widgetOrder.indexOf(b.id);                    
                    if (indexA === -1) return 1;
                    if (indexB === -1) return -1;
                    
                    return indexA - indexB;
                })
                .map(widget => ({
                    ...widget,
                    props: {
                        ...widget.props,
                        defaultToggle: this.getWidgetToggle(widget.id),
                        narrowVersion: true,
                        showLoadingToasts: false,
                    }
                }));
                
            return orderedWidgets;
        },
        navigateToWidget(id, offset = 127) {
            const componentRef = this.getComponentRef(id);
            if (!componentRef) return;
            
            const widgetSection = componentRef.$refs?.WidgetSection;

            if(widgetSection && widgetSection.toggle === false && typeof widgetSection.toggleSection === 'function') {
                widgetSection.toggleSection();
            }

            const element = componentRef.$el;
            if (!element) return;
            
            try {
                const scrollContainer = this.findScrollableParent(element);
                const containerRect = scrollContainer.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();
                const relativeTop = elementRect.top - containerRect.top;

                setTimeout(() => {
                    scrollContainer.scrollTo({
                        top: scrollContainer.scrollTop + relativeTop - offset,
                        behavior: 'smooth'
                    });
                }, 50);
            } catch (e) {
                console.error('Error during scroll:', e);
            }
        },
        closeWidget(id) {
            const componentRef = this.getComponentRef(id);
            if (!componentRef) return;

            const widgetSection = componentRef.$refs?.WidgetSection;

            if(widgetSection && widgetSection.toggle === true && typeof widgetSection.toggleSection === 'function') {
                widgetSection.toggleSection();
            }
        },
        openWidget(id) {
            const componentRef = this.getComponentRef(id);
            if (!componentRef) return;

            const widgetSection = componentRef.$refs?.WidgetSection;

            if(widgetSection && widgetSection.toggle === false && typeof widgetSection.toggleSection === 'function') {
                widgetSection.toggleSection();
            }
        },
        findScrollableParent(node) {
            if (node == null) {
                return document.documentElement;
            }
        
            if (node.scrollHeight > node.clientHeight) {
                return node;
            } else {
                return this.findScrollableParent(node.parentNode);
            }
        },
        resetWidgetStore() {
            this.componentRefs = {};
            this.$reset();
        }
    }
});
